import type { AllYouCanPlan, PlanBand, PlanTier } from '../store/useStore'

/**
 * #2039 時間制プランの表示まわり。**金額の計算はしない** — 金額の定義はサーバ
 * (services/pay/api/services/time_charge.py) が正で、画面は返ってきた値を写すだけ。
 * ここに置くのは「設定の入力を整える」「時刻を書く」だけ。
 */

export const UNIT_MINUTE_OPTIONS = [10, 15, 30, 60] as const
export const MAX_TIERS = 3
/** 時間帯の数の上限。サーバの `time_charge.MAX_BANDS` と同じ値 */
export const MAX_BANDS = 4
/** 上限 (時間) の最大。サーバの `all_you_can.MAX_DURATION_MINUTES` (12 時間) と同じ値 */
export const MAX_CAP_HOURS = 12

export const isTimePlan = (p?: Pick<AllYouCanPlan, 'pricing'> | null): boolean =>
  p?.pricing === 'time'

/** HH:MM。残り分数ではなく時刻で出す (画面を開いたままでも古くならない) */
export const hhmm = (iso?: string | null): string => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/**
 * 入力中の段 (文字列) を保存できる形へ。**両方とも空の行だけ**捨てる。
 *
 * 片方だけ入れた行 (「120 分以降は ¥＿」) を黙って捨てると、店主は段を入れたつもりのまま
 * 保存が通り、客には 120 分以降も満額が付く。半端な行は `tierProblem` がエラーにする。
 */
export const normalizeTiers = (
  rows: { afterMinutes: string; priceYen: string }[],
): PlanTier[] =>
  rows
    .filter((r) => r.afterMinutes.trim() !== '' || r.priceYen.trim() !== '')
    .map((r) => ({
      afterMinutes: r.afterMinutes.trim() === '' ? NaN : Number(r.afterMinutes),
      priceYen: r.priceYen.trim() === '' ? NaN : Number(r.priceYen),
    }))
    .sort((a, b) => (a.afterMinutes || 0) - (b.afterMinutes || 0))

/** #2052 曜日・時間帯で単価が変わるプランか (経過時間の段とは同時に使えない) */
export const usesClockRates = (
  p?: Pick<AllYouCanPlan, 'bands' | 'weekendDays' | 'weekendPriceYen'> | null,
): boolean => !!p && ((p.bands?.length ?? 0) > 0 || (p.weekendDays?.length ?? 0) > 0 || p.weekendPriceYen != null)

/** 入力中の料金表 (文字列)。 */
export interface ClockRatesForm {
  weekendDays: number[]
  weekendPriceYen: string
  bands: { startTime: string; priceYen: string; weekendPriceYen: string }[]
}

export const EMPTY_CLOCK_RATES: ClockRatesForm = { weekendDays: [], weekendPriceYen: '', bands: [] }

export const clockRatesFormOf = (
  p: Pick<AllYouCanPlan, 'bands' | 'weekendDays' | 'weekendPriceYen'>,
): ClockRatesForm => ({
  weekendDays: [...(p.weekendDays ?? [])].sort((a, b) => a - b),
  weekendPriceYen: p.weekendPriceYen == null ? '' : String(p.weekendPriceYen),
  bands: (p.bands ?? []).map((b) => ({
    startTime: b.startTime,
    priceYen: String(b.priceYen),
    weekendPriceYen: b.weekendPriceYen == null ? '' : String(b.weekendPriceYen),
  })),
})

/**
 * 入力中の料金表を保存できる形へ。週末の曜日を選んでいないときは、週末の金額を**送らない** —
 * 欄を隠しているだけで値が残っていると、サーバの「曜日が無いのに週末の単価がある」に当たる。
 */
export const normalizeClockRates = (f: ClockRatesForm): {
  bands: PlanBand[]; weekendDays: number[]; weekendPriceYen: number | null
} => {
  const weekend = f.weekendDays.length > 0
  const num = (s: string) => (s.trim() === '' ? null : Number(s))
  return {
    weekendDays: f.weekendDays,
    weekendPriceYen: weekend ? num(f.weekendPriceYen) : null,
    bands: f.bands.map((b) => ({
      startTime: b.startTime,
      priceYen: b.priceYen.trim() === '' ? NaN : Number(b.priceYen),
      weekendPriceYen: weekend ? num(b.weekendPriceYen) : null,
    })),
  }
}

/**
 * 料金表の入力の誤り。サーバも同じ検証をする (`time_charge._validate_clock_rates`)。
 * 返り値は i18n キーの末尾 (`storeAllYouCan.clockError.<key>`)。問題なければ null。
 */
export const clockRatesProblem = (
  rates: { bands: PlanBand[]; weekendDays: number[]; weekendPriceYen: number | null },
): string | null => {
  if (rates.bands.length > MAX_BANDS) return 'tooMany'
  const seen = new Set<string>()
  for (const b of rates.bands) {
    if (!/^\d{2}:\d{2}$/.test(b.startTime) || !Number.isFinite(b.priceYen)) return 'incomplete'
    if (seen.has(b.startTime)) return 'duplicate'
    seen.add(b.startTime)
  }
  // 週末の曜日を選んだら、週末の欄は全部埋める (基本も、どの時間帯も)。空の欄を平日の単価で
  // 代用すると、金曜 21:50 の ¥660 が 22:00 に ¥610 へ下がる料金表が黙って通る。
  if (rates.weekendDays.length > 0
    && (rates.weekendPriceYen == null || rates.bands.some((b) => b.weekendPriceYen == null))) {
    return 'weekendNeedsPrice'
  }
  // 何も入れていない (曜日も時間帯も無い) のに「曜日・時間帯で変わる」を選んでいる
  if (rates.weekendDays.length === 0 && rates.bands.length === 0) return 'empty'
  return null
}

/** 料金表の中で一番安い単価 (一覧の「¥550〜」用)。料金表が無ければ基本の単価そのもの。 */
export const lowestUnitPrice = (
  p: Pick<AllYouCanPlan, 'priceYen' | 'bands' | 'weekendDays' | 'weekendPriceYen'>,
): number => {
  const weekend = (p.weekendDays?.length ?? 0) > 0
  const prices = [p.priceYen, ...(p.bands ?? []).map((b) => b.priceYen)]
  if (weekend) {
    if (p.weekendPriceYen != null) prices.push(p.weekendPriceYen)
    for (const b of p.bands ?? []) if (b.weekendPriceYen != null) prices.push(b.weekendPriceYen)
  }
  return Math.min(...prices)
}

/** 営業日の中での並び順 (切り替え 6:00 なら 22:00 → 翌 1:00)。一覧と編集画面の表示に使う。 */
export const sortBandsByBusinessDay = <T extends { startTime: string }>(bands: T[], startHour: number): T[] => {
  const pos = (hhmm: string) => {
    const [h, m] = hhmm.split(':').map(Number)
    return (((h * 60 + m) - startHour * 60) % 1440 + 1440) % 1440
  }
  return [...bands].sort((a, b) => pos(a.startTime) - pos(b.startTime))
}

/** プランの料金の書き方。`¥660/ 30分・1名` (時間で課金) / `¥1,500/ 1名 · 120分` (定額) */
export const planPriceLabel = (
  p: Pick<AllYouCanPlan, 'pricing' | 'priceYen' | 'unitMinutes' | 'durationMinutes'
    | 'bands' | 'weekendDays' | 'weekendPriceYen'>,
  t: (key: string, params?: Record<string, string | number>) => string,
): string =>
  isTimePlan(p)
    // 曜日・時間帯で変わるプランは、**一番安い単価**に「〜」を付ける (基本が一番安いとは限らない —
    // 早い時間だけ安くする店もある)
    ? `¥${lowestUnitPrice(p).toLocaleString()}${usesClockRates(p) ? '〜' : ''}${t('storeAllYouCan.perUnitPerPerson', { unit: String(p.unitMinutes ?? 30) })}`
    : `¥${p.priceYen.toLocaleString()}${t('storeAllYouCan.perPerson')} · ${t('storeAllYouCan.minutes', { count: String(p.durationMinutes) })}`

/**
 * 段の入力の誤り。サーバも同じ検証をする (services/time_charge.validate_plan) が、
 * 保存を押す前に理由を出す — 無言で 400 になると店主には故障に見える。
 * 返り値は i18n キーの末尾 (`storeAllYouCan.tierError.<key>`)。問題なければ null。
 */
export const tierProblem = (
  unitMinutes: number, tiers: PlanTier[], capMinutes?: number,
): string | null => {
  if (tiers.length > MAX_TIERS) return 'tooMany'
  let last = 0
  for (const t of tiers) {
    if (!Number.isFinite(t.afterMinutes) || !Number.isFinite(t.priceYen)) return 'incomplete'
    // 上限より後に始まる段は一度も効かない (入れたつもりの割引が付かない)
    if (capMinutes && t.afterMinutes >= capMinutes) return 'beyondCap'
    if (t.afterMinutes <= last) return 'order'
    // 単位の途中で単価が変わると「その単位はいくらか」が決まらない
    if (t.afterMinutes % unitMinutes !== 0) return 'notMultiple'
    if (t.priceYen < 0) return 'price'
    last = t.afterMinutes
  }
  return null
}
